import { DatePicker, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import * as dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { Line, Pie } from 'react-chartjs-2';
import { useSelector } from 'react-redux';
import statsApi from '../../apis/stats';
import storeApi from '../../apis/store';
import userApi from '../../apis/user';
import { TrackingFilter } from '../../constants/order';
import { useHasMounted } from '../../hooks';
import { OrderType } from '../../interfaces/Order';
import { IStore } from '../../interfaces/Store';
import {
  ProductTypeStatsItem,
  ProductTypeStatsResponse,
  StatsResponse,
} from '../../interfaces/Stats';
import { RootState } from '../../reducers';
import { IAuthState } from '../../reducers/auth';
import { Role } from '../../role/role';
import { FlexContainer, PageContainer, TextButton } from '../../styles/Global';
import { SpinContainer, StyledSelect } from '../Order/Order.styled';
import PageHeader from '../PageHeader/PageHeader';
import CardStats from './CardStats';
import {
  CardStatsContainer,
  ChartContainer,
  ChartLegend,
  ChartWrapper,
  ConditionContainer,
  LegendDot,
  LegendItem,
  LegendLabel,
  ProductTypeStatsSection,
  ProductTypeTableWrapper,
  SearchFilterContainer,
  SideChartsContainer,
  StatsContainer,
  VerticalLine,
  Wrapper,
} from './Statistics.styled';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title
);

// Fixed color mapping for order statuses
const ORDER_STATUS_COLORS: Record<string, string> = {
  pending: '#1E90FF', // Blue
  processing: '#1C2951', // Dark Navy
  shipped: '#FF6B47', // Orange
  reshipped: '#4CAF50', // Green
  cancelled: '#E5E7EB', // Light Gray
};

const ORDER_STATUS_DISPLAY_NAMES: Record<string, string> = {
  pending: 'Pending',
  processing: 'Processing',
  shipped: 'Shipped',
  reshipped: 'Reshipped',
  cancelled: 'Cancelled',
};

const PRODUCT_TYPE_DISPLAY_NAMES: Record<OrderType, string> = {
  [OrderType.Sticker]: 'Sticker',
  [OrderType.MagnetSticker]: 'Magnet Sticker',
  [OrderType.PhoneCase]: 'Phone Case',
  [OrderType.Poster]: 'Poster',
  [OrderType.UVDTF]: 'UVDTF',
  [OrderType.BadgeReel]: 'Badge Reel',
  [OrderType.GlassSuncatcher]: 'Glass Suncatcher',
  [OrderType.SquishyBelly]: 'Squishy Belly',
  [OrderType.CardSkin]: 'Card Skin',
  [OrderType.Ornament]: 'Ornament',
};

// Line chart configuration
const getLineChartOptions = (color: string = '#1E90FF') => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      border: {
        display: false,
      },
    },
    y: {
      grid: {
        color: '#f3f4f6',
      },
      border: {
        display: false,
      },
      beginAtZero: true,
    },
  },
  elements: {
    point: {
      radius: 4,
      backgroundColor: color,
      borderColor: color,
      borderWidth: 2,
    },
    line: {
      borderColor: color,
      borderWidth: 3,
      fill: true,
      backgroundColor: `${color}1A`, // Adding transparency
    },
  },
});

// Format month string to abbreviated month name (Jan, Feb, Mar, etc.)
const formatMonthLabel = (monthStr: string): string => {
  try {
    // Handle common month formats

    // If it's already in abbreviated format (Jan, Feb, etc.), return as is
    const shortMonths = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    if (shortMonths.includes(monthStr)) {
      return monthStr;
    }

    // If it's in format like "2024-01" or "2024/01", parse it
    if (monthStr.includes('-') || monthStr.includes('/')) {
      const separator = monthStr.includes('-') ? '-' : '/';
      const parts = monthStr.split(separator);
      if (parts.length >= 2) {
        const monthIndex = parseInt(parts[1]) - 1; // Month is 0-indexed in Date
        if (monthIndex >= 0 && monthIndex <= 11) {
          return shortMonths[monthIndex];
        }
      }
    }

    // If it's a month number (1-12), convert it
    const monthNum = parseInt(monthStr);
    if (monthNum >= 1 && monthNum <= 12) {
      return shortMonths[monthNum - 1];
    }

    // Try to parse as a full date string
    const date = new Date(monthStr);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', { month: 'short' });
    }

    // If all else fails, return the original string
    return monthStr;
  } catch (error) {
    return monthStr;
  }
};

const createLineChartData = (
  data: { month: string; data: number }[],
  label: string,
  color: string = '#1E90FF'
) => ({
  labels: data?.map((item) => formatMonthLabel(item.month)) || [],
  datasets: [
    {
      label,
      data: data?.map((item) => item.data) || [],
      borderColor: color,
      backgroundColor: `${color}1A`, // Adding transparency
      borderWidth: 3,
      pointBackgroundColor: color,
      pointBorderColor: color,
      pointRadius: 4,
      pointBorderWidth: 2,
      fill: true,
    },
  ],
});

type ProductTypeTableRow = ProductTypeStatsItem & {
  key: OrderType;
};

const usdCurrencyFormatter = new Intl.NumberFormat('en-US', {
  currency: 'USD',
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
  style: 'currency',
});

const productTypeTableColumns: ColumnsType<ProductTypeTableRow> = [
  {
    title: 'Product Type',
    dataIndex: 'type',
    key: 'type',
    render: (type: OrderType) => PRODUCT_TYPE_DISPLAY_NAMES[type] ?? type,
  },
  {
    title: 'Total Orders',
    dataIndex: 'totalOrders',
    key: 'totalOrders',
    align: 'right',
  },
  {
    title: 'Total Cost',
    dataIndex: 'totalCost',
    key: 'totalCost',
    align: 'right',
    render: (totalCost: number) => usdCurrencyFormatter.format(totalCost),
  },
];

const createRequestKey = (scope: string, query: Record<string, string>) =>
  JSON.stringify({ scope, query });

const Statistics = () => {
  const { currentUser } = useSelector(
    (state: RootState) => state.auth as IAuthState
  );
  const isAdmin = currentUser?.role === Role.ADMIN;
  const [orderType, setOrderType] = useState<OrderType>(OrderType.Sticker);
  const [trackingFilter, setTrackingFilter] = useState<TrackingFilter>();
  const [filterDate, setFilterDate] = useState<{
    startDate: any;
    endDate: any;
  }>();
  const [statsData, setStatsData] = useState<StatsResponse>({
    totalShippedOrders: 0,
    totalRevenue: 0,
    totalCost: 0,
    totalOrders: 0,
    totalCancelledOrders: 0,
    totalReshippedOrders: 0,
    totalReshippedOrdersCost: 0,
    totalRevenueByMonthsDataset: [],
    totalCostByMonthDataset: [],
    totalCostByStatusDataset: [],
    totalShippedOrdersByMonthsDataset: [],
    totalReshippedOrdersByMonthDataset: [],
    totalOrdersByMonthDataset: [],
    totalOrdersByStatusDataset: { data: [], labels: [] },
  });
  const [productTypeStatsData, setProductTypeStatsData] =
    useState<ProductTypeStatsResponse>({
      data: [],
    });
  const [loading, setLoading] = useState(false);
  const [userOption, setUserOption] = useState<
    { label: string; value: string }[]
  >([]);
  const [username, setUsername] = useState('');
  const [stores, setStores] = useState<IStore[]>([]);
  const [storeId, setStoreId] = useState<string | undefined>();
  const [activeChart, setActiveChart] = useState<
    'orders' | 'revenue' | 'shipped' | 'reshipped' | 'cost'
  >('orders');
  const lastStatsRequestKeyRef = useRef<string>();
  const lastProductTypeStatsRequestKeyRef = useRef<string>();
  const mounted = useHasMounted();

  useEffect(() => {
    if (!mounted() || !isAdmin) {
      return;
    }

    fetchUser();
  }, [isAdmin, mounted]);

  useEffect(() => {
    if (!mounted()) {
      return;
    }
    loadStores();
  }, [mounted, isAdmin, username]);

  const fetchUser = async () => {
    try {
      const res = await userApi.listUsers({ page: 1, perPage: 50, all: true });
      const tmp = res.items.map((user) => {
        return { label: user.name, value: user.username };
      });
      tmp.unshift({ label: 'View All', value: '' });
      setUserOption(tmp);
    } catch (error) {
      setUserOption([]);
      console.error(error);
    } finally {
    }
  };

  const loadStores = async () => {
    try {
      if (!isAdmin) {
        const list = await storeApi.listStores();
        setStores(list);
        return;
      }

      if (!username) {
        setStores([]);
        setStoreId(undefined);
        return;
      }

      const users = await userApi.listUsers({ all: true });
      const seller = users.items.find((u) => u.username === username);
      if (!seller?.id) {
        setStores([]);
        return;
      }
      const list = await storeApi.listStoresForSeller(seller.id);
      setStores(list);
    } catch (error) {
      console.error(error);
      setStores([]);
    }
  };

  const onReset = () => {
    setFilterDate({
      startDate: null,
      endDate: null,
    });
    setTrackingFilter(undefined);
    setUsername('');
    setStoreId(undefined);
    setOrderType(OrderType.Sticker);
  };

  const handleStats = async () => {
    try {
      if (!isAdmin && !currentUser?.username) return;
      const productTypeQuery: Record<string, string> = {};
      if (filterDate?.startDate) {
        productTypeQuery.fromDate = filterDate.startDate;
      }
      if (filterDate?.endDate) {
        productTypeQuery.toDate = filterDate.endDate;
      }
      if (trackingFilter) {
        productTypeQuery.tracking = trackingFilter;
      }
      if (storeId) {
        productTypeQuery.storeId = storeId;
      }
      const query: Record<string, string> = {
        ...productTypeQuery,
        orderType: orderType,
      };
      const statsScope =
        isAdmin && !!username
          ? `seller:${username}:store:${storeId || 'all'}`
          : isAdmin
          ? `all-sellers:store:${storeId || 'all'}`
          : `seller:${currentUser.username}:store:${storeId || 'all'}`;
      const productTypeStatsScope =
        isAdmin && !!username
          ? `seller-product-types:${username}:store:${storeId || 'all'}`
          : isAdmin
          ? `all-seller-product-types:store:${storeId || 'all'}`
          : `seller-product-types:${currentUser.username}:store:${
              storeId || 'all'
            }`;
      const statsRequestKey = createRequestKey(statsScope, query);
      const productTypeStatsRequestKey = createRequestKey(
        productTypeStatsScope,
        productTypeQuery
      );
      const shouldFetchStats =
        lastStatsRequestKeyRef.current !== statsRequestKey;
      const shouldFetchProductTypeStats =
        lastProductTypeStatsRequestKeyRef.current !== productTypeStatsRequestKey;

      if (!shouldFetchStats && !shouldFetchProductTypeStats) {
        return;
      }

      setLoading(true);
      const statsRequest = !shouldFetchStats
        ? Promise.resolve(undefined)
        : !isAdmin
        ? statsApi.stats(query)
        : !!username
        ? statsApi.userStats(username, query)
        : statsApi.stats(query);
      const productTypeStatsRequest = !shouldFetchProductTypeStats
        ? Promise.resolve(undefined)
        : isAdmin && !!username
          ? statsApi.userProductTypeStats(username, productTypeQuery)
          : statsApi.productTypeStats(productTypeQuery);
      const [res, productTypeRes] = await Promise.all([
        statsRequest,
        productTypeStatsRequest,
      ]);
      if (res) {
        setStatsData(res);
        lastStatsRequestKeyRef.current = statsRequestKey;
      }
      if (productTypeRes) {
        setProductTypeStatsData(productTypeRes);
        lastProductTypeStatsRequestKeyRef.current = productTypeStatsRequestKey;
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const productTypeTableRows = productTypeStatsData.data.map((row) => ({
    ...row,
    key: row.type,
  }));

  return (
    <PageContainer>
      {loading && (
        <SpinContainer>
          <Spin size="large" />
        </SpinContainer>
      )}
      <PageHeader title="Statistics" />

      <SearchFilterContainer>
        <ConditionContainer $isAdmin={isAdmin}>
          <DatePicker.RangePicker
            onChange={(val) => {
              if (Array.isArray(val) && val.length > 1) {
                setFilterDate({
                  startDate: dayjs.default(val[0]).toISOString(),
                  endDate: dayjs.default(val[1]).toISOString(),
                });
              } else {
                setFilterDate({
                  startDate: null,
                  endDate: null,
                });
              }
            }}
            allowClear
          />
          <VerticalLine />
          <StyledSelect
            className="w-100"
            placeholder="Choose Product"
            onChange={(val: any) => {
              setOrderType(val);
            }}
            options={[
              { label: 'Sticker', value: OrderType.Sticker },
              { label: 'Magnet Sticker', value: OrderType.MagnetSticker },
              { label: 'Phone Case', value: OrderType.PhoneCase },
              { label: 'Poster', value: OrderType.Poster },
              { label: 'UVDTF', value: OrderType.UVDTF },
              { label: 'Badge Reel', value: OrderType.BadgeReel },
              { label: 'Glass Suncatcher', value: OrderType.GlassSuncatcher },
              { label: 'Squishy Belly', value: OrderType.SquishyBelly },
              { label: 'Card Skin', value: OrderType.CardSkin },
              { label: 'Ornament', value: OrderType.Ornament },
            ]}
            value={orderType}
          />
          <StyledSelect
            className="w-100"
            placeholder="Choose Tracking Filter"
            onChange={(val: any) => {
              setTrackingFilter(val);
            }}
            options={[
              { label: 'All', value: '' },
              {
                label: 'Mua shipment của mình',
                value: TrackingFilter.ShipmentOnly,
              },
            ]}
            value={trackingFilter}
          />
          {isAdmin && (
            <StyledSelect
              className="w-100"
              placeholder="Choose user"
              onChange={(val: any) => {
                setUsername(val);
                setStoreId(undefined);
              }}
              options={userOption}
              value={username}
            />
          )}
          {(!isAdmin || !!username) && (
            <StyledSelect
              className="w-100"
              placeholder="Store"
              allowClear
              onChange={(val: any) => {
                setStoreId(val || undefined);
              }}
              options={[
                { label: 'All stores', value: '' },
                { label: 'Unassigned', value: 'unassigned' },
                ...stores.map((store) => ({
                  label: store.isDefault
                    ? `${store.name} (default)`
                    : store.name,
                  value: store.id,
                })),
              ]}
              value={storeId}
            />
          )}
        </ConditionContainer>
        <FlexContainer $gap={16}>
          <TextButton onClick={handleStats}>Apply</TextButton>
          <TextButton $cancel onClick={onReset}>
            Reset
          </TextButton>
        </FlexContainer>
      </SearchFilterContainer>
      <StatsContainer>
        <Wrapper>
          <h3>Overview</h3>
          <CardStatsContainer
            isShipmentOnly={trackingFilter === TrackingFilter.ShipmentOnly}
          >
            <CardStats
              title="Total Orders"
              value={statsData.totalOrders}
              onClick={() => setActiveChart('orders')}
              isActive={activeChart === 'orders'}
            />
            <CardStats
              title="Total Reshipped Orders"
              value={statsData.totalReshippedOrders}
              onClick={() => setActiveChart('reshipped')}
              isActive={activeChart === 'reshipped'}
            />
            <CardStats
              title="Total Shipped Orders"
              value={statsData.totalShippedOrders}
              onClick={() => setActiveChart('shipped')}
              isActive={activeChart === 'shipped'}
            />
            {trackingFilter === TrackingFilter.ShipmentOnly && (
              <CardStats
                title="Total Shipping Cost"
                value={statsData.totalShippingCost || 0}
              />
            )}
            <CardStats
              title="Total Cost"
              value={statsData.totalCost}
              onClick={() => setActiveChart('cost')}
              isActive={activeChart === 'cost'}
            />
          </CardStatsContainer>

          {/* Dynamic Line Chart based on selected card */}
          <div style={{ marginTop: '24px', height: '300px' }}>
            {activeChart === 'orders' && (
              <Line
                options={getLineChartOptions('#1E90FF')}
                data={createLineChartData(
                  statsData.totalOrdersByMonthDataset,
                  'Total Orders',
                  '#1E90FF'
                )}
              />
            )}
            {activeChart === 'cost' && (
              <Line
                options={getLineChartOptions('#4CAF50')}
                data={createLineChartData(
                  statsData.totalCostByMonthDataset,
                  'Cost',
                  '#4CAF50'
                )}
              />
            )}
            {activeChart === 'shipped' && (
              <Line
                options={getLineChartOptions('#FF6B47')}
                data={createLineChartData(
                  statsData.totalShippedOrdersByMonthsDataset,
                  'Shipped Orders',
                  '#FF6B47'
                )}
              />
            )}
            {activeChart === 'reshipped' && (
              <Line
                options={getLineChartOptions('#1C2951')}
                data={createLineChartData(
                  statsData.totalReshippedOrdersByMonthDataset,
                  'Reshipped Orders',
                  '#1C2951'
                )}
              />
            )}
          </div>
        </Wrapper>

        <SideChartsContainer>
          <Wrapper>
            <h3>Order Status</h3>
            <ChartWrapper>
              <ChartContainer>
                <Pie
                  height="200px"
                  width="200px"
                  options={{
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false, // We'll create a custom legend
                      },
                    },
                    cutout: '60%', // This creates the donut effect
                  }}
                  data={{
                    labels: statsData.totalOrdersByStatusDataset.labels,
                    datasets: [
                      {
                        label: '# Total',
                        data: statsData.totalOrdersByStatusDataset.data || [],
                        backgroundColor:
                          statsData.totalOrdersByStatusDataset.labels?.map(
                            (label: string) => {
                              return (
                                ORDER_STATUS_COLORS[label.toLowerCase()] ||
                                '#E5E7EB'
                              );
                            }
                          ) || [],
                        borderColor:
                          statsData.totalOrdersByStatusDataset.labels?.map(
                            (label: string) => {
                              return (
                                ORDER_STATUS_COLORS[label.toLowerCase()] ||
                                '#E5E7EB'
                              );
                            }
                          ) || [],
                        borderWidth: 0,
                      },
                    ],
                  }}
                />
              </ChartContainer>
              {/* Custom Legend */}
              <ChartLegend>
                {statsData.totalOrdersByStatusDataset.labels?.map(
                  (label: string) => {
                    const statusKey = label.toLowerCase();
                    const color = ORDER_STATUS_COLORS[statusKey] || '#E5E7EB';

                    return (
                      <LegendItem key={label}>
                        <LegendDot color={color} />
                        <LegendLabel>
                          {ORDER_STATUS_DISPLAY_NAMES[statusKey] || label}
                        </LegendLabel>
                      </LegendItem>
                    );
                  }
                )}
              </ChartLegend>
            </ChartWrapper>
          </Wrapper>

        </SideChartsContainer>

        {/* <Row>
          <ChartWrapper>
            <p>Stats Order By Status</p>
            <ChartContainer>
              <Pie
                height="200px"
                width="200px"
                options={{ maintainAspectRatio: false }}
                data={{
                  labels: statsData.totalOrdersByStatusDataset.labels,
                  datasets: [
                    {
                      label: '# Total',
                      data: statsData.totalOrdersByStatusDataset.data || [],
                      backgroundColor: [
                        'rgba(255, 99, 132, 0.2)',
                        'rgba(54, 162, 235, 0.2)',
                        'rgba(255, 206, 86, 0.2)',
                      ],
                      borderColor: [
                        'rgba(255, 99, 132, 1)',
                        'rgba(54, 162, 235, 1)',
                        'rgba(255, 206, 86, 1)',
                      ],
                      borderWidth: 1,
                    },
                  ],
                }}
              />
            </ChartContainer>
          </ChartWrapper>
          <ChartWrapper>
            <p>Stats Cost By Status</p>
            <ChartContainer>
              <Pie
                height="200px"
                width="200px"
                options={{ maintainAspectRatio: false }}
                data={{
                  labels: statsData.totalCostByStatusDataset.labels,
                  datasets: [
                    {
                      label: '# Total',
                      data: statsData.totalCostByStatusDataset.data || [],
                      backgroundColor: [
                        'rgba(255, 99, 132, 0.2)',
                        'rgba(54, 162, 235, 0.2)',
                        'rgba(255, 206, 86, 0.2)',
                        'rgba(75, 192, 192, 0.2)',
                        'rgba(153, 102, 255, 0.2)',
                        'rgba(255, 159, 64, 0.2)',
                      ],
                      borderColor: [
                        'rgba(255, 99, 132, 1)',
                        'rgba(54, 162, 235, 1)',
                        'rgba(255, 206, 86, 1)',
                        'rgba(75, 192, 192, 1)',
                        'rgba(153, 102, 255, 1)',
                        'rgba(255, 159, 64, 1)',
                      ],
                      borderWidth: 1,
                    },
                  ],
                }}
              />
            </ChartContainer>
          </ChartWrapper>
        </Row> */}
      </StatsContainer>
      <ProductTypeStatsSection>
        <h3>Product Type</h3>
        <ProductTypeTableWrapper>
          <Table
            columns={productTypeTableColumns}
            dataSource={productTypeTableRows}
            pagination={false}
            size="small"
            locale={{ emptyText: 'No product type data' }}
          />
        </ProductTypeTableWrapper>
      </ProductTypeStatsSection>
    </PageContainer>
  );
};

export default Statistics;
